import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AUTOPATROCINIO_KEY, RESGATE_KEY } from '../../../core/constants/storage-keys';
import { TIPO_INSTITUTO } from '../../../core/constants/tipo-instituto';
import { EscolhaAutopatrocinio } from '../../../core/models/autopatrocinio';
import { EscolhaResgate } from '../../../core/models/resgate';
import { PdfDocument } from '../../../shared/components/pdf-document/pdf-document';
import { HttpClientService } from '../../../shared/services/http-client-service';
import { LoaderService } from '../../../shared/services/loader-service';
import { LocalStorageService } from '../../../shared/services/local-storage-service';
import { ToastService } from '../../../shared/services/toast-service';
import { base64ParaPdf } from '../../../shared/utils/pdf';
import { ParametrosTermo, TermoResponse } from '../../../core/models/termo';

type IdInstitutoTermo = typeof TIPO_INSTITUTO.RESGATE | typeof TIPO_INSTITUTO.AUTOPATROCINIO | typeof TIPO_INSTITUTO.BPD;

/** Tela de origem de cada instituto, para onde o "Voltar" leva. */
const ROTA_VOLTAR: Record<IdInstitutoTermo, string> = {
  [TIPO_INSTITUTO.RESGATE]: '/instituto-online/resgate-mvp',
  [TIPO_INSTITUTO.AUTOPATROCINIO]: '/instituto-online/autopatrocinio',
  [TIPO_INSTITUTO.BPD]: '/instituto-online/escolha-instituto',
};

/** Lê o idInstituto da URL; sem ele ou com um valor desconhecido, assume o autopatrocínio. */
function idInstitutoDaUrl(valor: string | null): IdInstitutoTermo {
  const id = Number(valor);
  return id === TIPO_INSTITUTO.RESGATE || id === TIPO_INSTITUTO.BPD ? id : TIPO_INSTITUTO.AUTOPATROCINIO;
}

@Component({
  imports: [PdfDocument],
  selector: 'app-assinatura-termo-mvp',
  styleUrl: './assinatura-termo-mvp.scss',
  templateUrl: './assinatura-termo-mvp.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AssinaturaTermoMvp implements OnInit {
  private readonly router = inject(Router);
  private readonly httpClientService = inject(HttpClientService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly localStorageService = inject(LocalStorageService);

  /**
   * Instituto do termo, vindo da URL: ?idInstituto=1 (resgate), ?idInstituto=2 (autopatrocínio) ou
   * ?idInstituto=3 (BPD, pela escolha do instituto). Sem o parâmetro, assume o autopatrocínio.
   */
  private readonly idInstituto = idInstitutoDaUrl(inject(ActivatedRoute).snapshot.queryParamMap.get('idInstituto'));

  /** Falha ao buscar o termo. */
  readonly error = signal(false);
  /** Dados do termo retornados pela API (nome, cpf e tokens usados na assinatura). */
  readonly termo = signal<TermoResponse | null>(null);
  /** Endereço local (blob:) do PDF do termo; enquanto vazio, o visualizador não é renderizado. */
  readonly pdfSrc = signal<string | undefined>(undefined);
  /** O botão "Assinar termo" só é habilitado depois de marcar "Li e estou de acordo". */
  readonly aceiteTermo = signal(false);
  /** Modal "Solicitação enviada com sucesso!", aberto quando a assinatura é registrada. */
  readonly openModalSucesso = signal(false);

  constructor() {
    // Libera da memória o PDF criado com URL.createObjectURL ao sair da tela.
    inject(DestroyRef).onDestroy(() => this.liberarPdf());
  }

  ngOnInit(): void {
    void this.getTermo();
  }

  /** O vivest-checkbox dispara `change` e expõe o estado na propriedade `checked`. */
  protected onAceiteChange(event: Event): void {
    this.aceiteTermo.set((event.target as HTMLElement & { checked: boolean }).checked);
  }

  /**
   * Busca o termo e exibe o PDF, que vem em base64. No BPD envia só o idInstituto; no resgate e no
   * autopatrocínio envia também as escolhas salvas no localStorage e, sem elas (ex.: acesso direto
   * à página), não chama a API.
   */
  async getTermo(): Promise<void> {
    let params: ParametrosTermo | undefined;
    if (this.idInstituto !== TIPO_INSTITUTO.BPD) {
      params = this.parametrosDaEscolha();
      if (!params) {
        const instituto = this.idInstituto === TIPO_INSTITUTO.RESGATE ? 'do resgate' : 'do autopatrocínio';
        this.error.set(true);
        this.toastService.show(`Não encontramos as informações ${instituto}. Preencha-as novamente.`, 'error');
        return;
      }
    }

    this.loaderService.showLoader('Carregando...');

    try {
      const { body } = await this.httpClientService.getTermoMVP(this.idInstituto, params);
      if (!body?.pdf) throw new Error('Termo sem PDF');

      this.termo.set(body);
      this.liberarPdf();
      this.pdfSrc.set(URL.createObjectURL(base64ParaPdf(body.pdf)));
    } catch (e) {
      console.error(e);
      this.error.set(true);
      this.toastService.show('Não foi possível carregar o termo. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /** Escolhas salvas no localStorage pela tela do resgate ou do autopatrocínio, prontas para a query. */
  private parametrosDaEscolha(): ParametrosTermo | undefined {
    if (this.idInstituto === TIPO_INSTITUTO.RESGATE) {
      return this.localStorageService.getObjectStorageDecrypt<EscolhaResgate>(RESGATE_KEY) ?? undefined;
    }
    return this.localStorageService.getObjectStorageDecrypt<EscolhaAutopatrocinio>(AUTOPATROCINIO_KEY) ?? undefined;
  }

  /** O BPD veio direto da escolha do instituto; o resgate e o autopatrocínio voltam para a própria tela. */
  onReturn() {
    this.router.navigate([ROTA_VOLTAR[this.idInstituto]]);
  }

  /**
   * Assina o termo enviando o idInstituto da URL e o processId retornado pelo getTermo e, com
   * sucesso, abre o modal de confirmação. Sem o processId (o termo não carregou), não chama a API.
   */
  async onSubmit(): Promise<void> {
    if (!this.aceiteTermo()) return;

    const processId = this.termo()?.processId;
    if (!processId) {
      this.toastService.show('Não foi possível assinar o termo. Recarregue a página e tente novamente.', 'error');
      return;
    }

    this.loaderService.showLoader('Enviando...');

    try {
      await this.httpClientService.assinarTermoMVP(this.idInstituto, processId);
      this.openModalSucesso.set(true);
    } catch {
      this.toastService.show('Não foi possível assinar o termo. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

  closeModalSucesso(): void {
    this.openModalSucesso.set(false);
  }

  onClick(): void {
    this.closeModalSucesso();
    this.router.navigate(['/instituto-online/detalhes-mvp']);
  }

  private liberarPdf(): void {
    const src = this.pdfSrc();
    if (src) URL.revokeObjectURL(src);
  }
}
